import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { Button, Icon, Popconfirm, Table, message } from 'antd'
import { PageHeader } from '../Common'
import { useAsync } from '../../hooks'
import { useDevices, useFetchDevices } from '../../redux/slices/devices'
import { useIsAdmin } from '../../redux/slices/session'
import api from '../../api'
import Loading from '../Loading'
import SetDeviceModal from './SetDeviceModal'

const useStyles = createUseStyles({
  ok: {
    color: 'green',
  },
  notOk: {
    color: 'red',
  },
  container: {
    width: '70%',
    margin: '0 auto',
    marginBottom: '1rem',
  },
  center: {
    textAlign: 'center',
  },
  newDeviceContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: 'solid 1px #e8e8e8',
  },
  newLocation: {
    width: '100%',
    margin: '1rem',
  },
  rows: {
    '& td:last-child': {
      width: '5%',
      textAlign: 'center',
    },
  },
})

export default function Devices() {
  const { id } = useParams()
  const history = useHistory()
  const classes = useStyles()
  const isAdmin = useIsAdmin()
  const [loading, setLoading] = useState('none')
  const [deviceChangeVisible, setDeviceChangeVisible] = useState(false)
  const [deviceToEdit, setDeviceToEdit] = useState({})
  const getPatient = useCallback(async () => {
    const patient = await api.patients.get(id)

    return patient
  }, [id])

  const { pending, value: patient } = useAsync(getPatient)
  const patientDevices = useDevices(id)
  const fetchDevices = useFetchDevices(id)
  const showDeviceModal = device => {
    setDeviceToEdit(device)
    setDeviceChangeVisible(true)
  }
  const handleDeleteDevice = async deviceId => {
    try {
      await api.devices.deleteDevice(deviceId)
      message.success('Device deleted successfully')
    } catch (e) {
      message.error(`There was an error deleting the device: ${e.message}`)
    }
    fetchDevices()
  }

  useEffect(() => {
    setLoading('fetch')
    fetchDevices()
    setLoading('none')
  }, [id, fetchDevices])

  const columns = [
    { title: 'Serial Number', dataIndex: 'serialNumber', key: 'serialNumber' },
    { title: 'Model', dataIndex: 'modelName', key: 'modelName' },
    { title: 'Type', dataIndex: 'modelType', key: 'modelType' },
    {
      title: 'Current',
      dataIndex: 'device.current',
      render(text, device) {
        return (
          <Icon
            type={`${device.current ? 'check' : 'close'}-circle`}
            className={classes[device.current ? 'ok' : 'notOk']}
          />
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render(text, device) {
        return (
          <>
            <Button
              type="link"
              icon="edit"
              loading={loading === 'delete'}
              onClick={() => showDeviceModal(device)}
            />
            <Popconfirm
              title="Are you sure you want to delete this device?"
              onConfirm={() => handleDeleteDevice(device.id)}
              placement="topLeft"
            >
              <Button
                type="link"
                icon="delete"
                loading={loading === 'delete'}
              />
            </Popconfirm>
          </>
        )
      },
    },
  ]

  const handleDeviceChanged = changed => {
    setDeviceChangeVisible(false)
    fetchDevices()
  }

  if (pending) return <Loading large />

  return (
    <div>
      <PageHeader
        title={`Devices - ${patient.firstName} ${patient.lastName}`}
        onBack={history.goBack}
      />
      <div className={classes.container}>
        {isAdmin && (
          <div className={classes.newDeviceContainer}>
            <Button
              type="primary"
              onClick={() => showDeviceModal({})}
              loading={loading === 'create'}
            >
              Add New Device
            </Button>
          </div>
        )}
        <Table
          columns={columns}
          dataSource={patientDevices}
          loading={loading === 'fetch'}
          rowClassName={classes.rows}
          rowKey="id"
        />
        <SetDeviceModal
          patientId={id}
          deviceToEdit={deviceToEdit}
          onDeviceChanged={handleDeviceChanged}
          visible={deviceChangeVisible}
        />
        <div className={classes.center}>
          <Button type="primary" onClick={() => history.goBack()}>
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}
